/**
 * @file FX de tir volumineux (boutique) : couche qui se superpose au skin de munition. Trois moments, chacun compose de briques de donnees (`ShotFxStyle`) :
 *  - bouche du canon (muzzle) : flash geant, cone de flammes, anneaux supersoniques, etoile, canon a confettis, spirale, eventail de prisme ;
 *  - traine du projectile (trail) : cometes, orbes, helices, zigzags, braises, pixels, anneaux, petales, coeurs, bulles, gouttes, confettis ;
 *  - impact (impact[]) : boule de feu, ondes de choc, pilier de lumiere, eclats, eclairs, implosion, pluie de meteores, cratere, floraison, pics de glace,
 *    nova, anneau de fumee, tornade, pixels, anneaux arc-en-ciel...
 * Tout passe par un lot de quads (SpriteBatch) et un lot de rubans (RibbonBatch) : 2 draw calls, tampons pre-alloues, particules en tableaux (aucune allocation
 * par frame). Budgets : capacite 1100 particules (moitie en eco), impacts espaces dans le temps.
 */
import { Vector3 } from 'three';
import type { PerspectiveCamera, Scene } from 'three';
import type { ShotFxStyle, ShotImpact } from '@config/types';
import type { EventBus, GameEvents } from '@core/index';
import { buildAtlas, Cell, hexRgb, hsv, RibbonBatch, SpriteBatch } from './FxBatch';
import type { CamBasis } from './FxBatch';
import type { Texture } from 'three';

const TAU = Math.PI * 2;
const CAP = 1100;
const RING_CAP = 40;
const BOLT_CAP = 24;
const PILLAR_CAP = 10;
const SCHED_CAP = 96;
const FLAT_Y = 0.07;
const BOLT_SEGMENTS = 9;
const MIN_IMPACT_GAP = 0.07;
const MIN_MUZZLE_GAP = 0.06;

type MuzzleLookup = (catId: number) => { x: number; y: number; z: number } | null;

const rnd = (a: number, b: number): number => a + Math.random() * (b - a);

// modes de particule
const M_BILL = 0;
const M_STREAK = 1;
const M_FACING = 2;
const M_ORBIT = 3;

/** Composants d'impact a executer plus tard (indices de ShotImpact). */
/** Indice special d'un composant differe : lancement d'un meteore. */
const METEOR_LAUNCH = 200;

const IMPACT_ORDER: readonly ShotImpact[] = ['fireball', 'shockwave', 'pillar', 'shards', 'bolts', 'implode', 'meteors', 'crater', 'bloom', 'spikes', 'bubbles', 'confetti', 'hearts', 'stars', 'nova', 'smokering', 'tornado', 'pixels', 'rainbowrings'];

export class ShotFxRenderer {
  private readonly atlas: Texture;
  private readonly sprites: SpriteBatch;
  private readonly ribbons: RibbonBatch;
  private unsubs: Array<() => void> = [];
  private styleOf: () => ShotFxStyle | null = () => null;
  private muzzleOf: MuzzleLookup = () => null;
  private eco = false;
  private clock = 0;
  private lastImpact = -1;
  private lastMuzzle = -1;
  private cur: ShotFxStyle | null = null;
  private readonly cam: CamBasis = { right: new Vector3(), up: new Vector3(), pos: new Vector3() };
  private readonly rgb = [1, 1, 1];
  private readonly c0: [number, number, number] = [1, 1, 1];
  private readonly c1: [number, number, number] = [1, 1, 1];
  private readonly c2: [number, number, number] = [1, 1, 1];

  // ---- particules (SoA)
  private n = 0;
  private readonly px = new Float32Array(CAP);
  private readonly py = new Float32Array(CAP);
  private readonly pz = new Float32Array(CAP);
  private readonly vx = new Float32Array(CAP);
  private readonly vy = new Float32Array(CAP);
  private readonly vz = new Float32Array(CAP);
  private readonly life = new Float32Array(CAP);
  private readonly maxLife = new Float32Array(CAP);
  private readonly s0 = new Float32Array(CAP);
  private readonly s1 = new Float32Array(CAP);
  private readonly rot = new Float32Array(CAP);
  private readonly spin = new Float32Array(CAP);
  private readonly cell = new Uint8Array(CAP);
  private readonly cr = new Float32Array(CAP);
  private readonly cg = new Float32Array(CAP);
  private readonly cb = new Float32Array(CAP);
  private readonly ca = new Float32Array(CAP);
  private readonly grav = new Float32Array(CAP);
  private readonly drag = new Float32Array(CAP);
  private readonly mode = new Uint8Array(CAP);
  private readonly ex = new Float32Array(CAP);
  private readonly ez = new Float32Array(CAP);
  private readonly orad = new Float32Array(CAP);

  // ---- anneaux au sol / face a la camera
  private readonly rActive = new Uint8Array(RING_CAP);
  private readonly rx = new Float32Array(RING_CAP);
  private readonly rz = new Float32Array(RING_CAP);
  private readonly rt = new Float32Array(RING_CAP);
  private readonly rmax = new Float32Array(RING_CAP);
  private readonly rr0 = new Float32Array(RING_CAP);
  private readonly rr1 = new Float32Array(RING_CAP);
  private readonly rcol = new Float32Array(RING_CAP * 3);
  private readonly ralpha = new Float32Array(RING_CAP);
  private readonly rcell = new Uint8Array(RING_CAP);
  private readonly rspin = new Float32Array(RING_CAP);
  private readonly rrain = new Uint8Array(RING_CAP);
  private rCursor = 0;

  // ---- eclairs
  private readonly bActive = new Uint8Array(BOLT_CAP);
  private readonly bx = new Float32Array(BOLT_CAP);
  private readonly bz = new Float32Array(BOLT_CAP);
  private readonly bangle = new Float32Array(BOLT_CAP);
  private readonly blen = new Float32Array(BOLT_CAP);
  private readonly bt = new Float32Array(BOLT_CAP);
  private readonly bmax = new Float32Array(BOLT_CAP);
  private readonly bseed = new Float32Array(BOLT_CAP);
  private readonly bcol = new Float32Array(BOLT_CAP * 3);
  private bCursor = 0;
  private readonly bxs = new Float32Array(BOLT_SEGMENTS + 1);
  private readonly bys = new Float32Array(BOLT_SEGMENTS + 1);
  private readonly bzs = new Float32Array(BOLT_SEGMENTS + 1);

  // ---- piliers de lumiere
  private readonly lActive = new Uint8Array(PILLAR_CAP);
  private readonly lx = new Float32Array(PILLAR_CAP);
  private readonly lz = new Float32Array(PILLAR_CAP);
  private readonly lt = new Float32Array(PILLAR_CAP);
  private readonly lmax = new Float32Array(PILLAR_CAP);
  private readonly lh = new Float32Array(PILLAR_CAP);
  private readonly lw = new Float32Array(PILLAR_CAP);
  private readonly lcol = new Float32Array(PILLAR_CAP * 3);
  private lCursor = 0;

  // ---- composants differes
  private readonly sActive = new Uint8Array(SCHED_CAP);
  private readonly sAt = new Float32Array(SCHED_CAP);
  private readonly sComp = new Uint8Array(SCHED_CAP);
  private readonly sx = new Float32Array(SCHED_CAP);
  private readonly sz = new Float32Array(SCHED_CAP);
  private readonly sk = new Float32Array(SCHED_CAP);

  constructor(private readonly scene: Scene) {
    this.atlas = buildAtlas();
    this.sprites = new SpriteBatch(scene, this.atlas, CAP + RING_CAP * 2 + PILLAR_CAP * 3 + 64, 6);
    this.ribbons = new RibbonBatch(scene, 700, 5);
  }

  /** Fournit le style equipe (lu a chaque evenement) et le lieu du canon. */
  bind(styleOf: () => ShotFxStyle | null, muzzleOf: MuzzleLookup): void {
    this.styleOf = styleOf;
    this.muzzleOf = muzzleOf;
  }

  setEco(eco: boolean): void {
    this.eco = eco;
  }

  /** Vrai si le FX equipe demande une traine (le rendu des projectiles doit alors echantillonner meme sans traine de skin). */
  get wantsTrail(): boolean {
    const s = this.styleOf();
    return s !== null && s.trail !== 'none';
  }

  get alive(): number {
    return this.n;
  }

  subscribe(bus: EventBus<GameEvents>): void {
    this.unsubs.push(
      bus.on('ProjectileFired', (e) => this.muzzle(e.catId, e.x, e.y, e.angle)),
      bus.on('BeamFired', (e) => {
        this.muzzle(e.catId, e.x0, e.y0, Math.atan2(e.y1 - e.y0, e.x1 - e.x0));
        this.beam(e.x0, e.y0, e.x1, e.y1);
      }),
      bus.on('EntityHit', (e) => this.impact(e.x, -e.y, e.crit ? 1.3 : 1)),
      bus.on('Explosion', (e) => this.impact(e.x, -e.y, 1.15 + Math.min(1.2, e.radius * 0.22), true)),
    );
  }

  // ------------------------------------------------------------------ outils

  private load(): number {
    return this.n / (this.eco ? CAP * 0.45 : CAP);
  }

  private setStyle(s: ShotFxStyle): void {
    if (this.cur === s) return;
    this.cur = s;
    const a = hexRgb(s.colors[0]);
    const b = hexRgb(s.colors[1]);
    const c = hexRgb(s.colors[2]);
    for (let i = 0; i < 3; i++) {
      this.c0[i] = a[i] as number;
      this.c1[i] = b[i] as number;
      this.c2[i] = c[i] as number;
    }
  }

  /** Couleur `which` (0, 1, 2) ou teinte qui defile si le FX est RVB ; ecrit dans this.rgb. */
  private col(s: ShotFxStyle, which: number, salt = 0): void {
    if (s.rainbow) {
      hsv(this.clock * 0.7 + which * 0.31 + salt * 0.137, 0.9, 1, this.rgb);
      return;
    }
    const c = which === 0 ? this.c0 : which === 1 ? this.c1 : this.c2;
    this.rgb[0] = c[0];
    this.rgb[1] = c[1];
    this.rgb[2] = c[2];
  }

  private spawn(cell: Cell, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, r: number, g: number, b: number, a: number, grav = 0, drag = 0, rot = 0, spin = 0, mode = M_BILL): number {
    if (this.n >= CAP) return -1;
    const i = this.n++;
    this.px[i] = x;
    this.py[i] = y;
    this.pz[i] = z;
    this.vx[i] = vx;
    this.vy[i] = vy;
    this.vz[i] = vz;
    this.life[i] = life;
    this.maxLife[i] = life;
    this.s0[i] = s0;
    this.s1[i] = s1;
    this.rot[i] = rot;
    this.spin[i] = spin;
    this.cell[i] = cell;
    this.cr[i] = r;
    this.cg[i] = g;
    this.cb[i] = b;
    this.ca[i] = a;
    this.grav[i] = grav;
    this.drag[i] = drag;
    this.mode[i] = mode;
    return i;
  }

  /** Particule coloree selon le style (couleur `which`). */
  private sp(s: ShotFxStyle, which: number, cell: Cell, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, a = 0.95, grav = 0, drag = 0, rot = 0, spin = 0, mode = M_BILL): number {
    this.col(s, which, this.n);
    return this.spawn(cell, x, y, z, vx, vy, vz, life, s0, s1, this.rgb[0] as number, this.rgb[1] as number, this.rgb[2] as number, a, grav, drag, rot, spin, mode);
  }

  private white(cell: Cell, x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, s0: number, s1: number, a = 1, grav = 0, drag = 0, rot = 0, spin = 0, mode = M_BILL): number {
    return this.spawn(cell, x, y, z, vx, vy, vz, life, s0, s1, 1, 1, 1, a, grav, drag, rot, spin, mode);
  }

  private ring(x: number, z: number, life: number, r0: number, r1: number, s: ShotFxStyle, which: number, alpha: number, cell: Cell = Cell.Ring, spin = 0, facing = false): void {
    const i = this.rCursor++ % RING_CAP;
    this.col(s, which, i);
    this.rActive[i] = 1;
    this.rx[i] = x;
    this.rz[i] = z;
    this.rt[i] = 0;
    this.rmax[i] = life;
    this.rr0[i] = r0;
    this.rr1[i] = r1;
    this.rcol[i * 3] = this.rgb[0] as number;
    this.rcol[i * 3 + 1] = this.rgb[1] as number;
    this.rcol[i * 3 + 2] = this.rgb[2] as number;
    this.ralpha[i] = alpha;
    this.rcell[i] = cell;
    this.rspin[i] = spin;
    this.rrain[i] = s.rainbow ? 1 : 0;
    void facing;
  }

  private bolt(x: number, z: number, angle: number, len: number, life: number, s: ShotFxStyle, which: number): void {
    const i = this.bCursor++ % BOLT_CAP;
    this.col(s, which, i);
    this.bActive[i] = 1;
    this.bx[i] = x;
    this.bz[i] = z;
    this.bangle[i] = angle;
    this.blen[i] = len;
    this.bt[i] = 0;
    this.bmax[i] = life;
    this.bseed[i] = Math.random() * 100;
    this.bcol[i * 3] = this.rgb[0] as number;
    this.bcol[i * 3 + 1] = this.rgb[1] as number;
    this.bcol[i * 3 + 2] = this.rgb[2] as number;
  }

  private pillar(x: number, z: number, life: number, h: number, w: number, s: ShotFxStyle, which: number): void {
    const i = this.lCursor++ % PILLAR_CAP;
    this.col(s, which, i);
    this.lActive[i] = 1;
    this.lx[i] = x;
    this.lz[i] = z;
    this.lt[i] = 0;
    this.lmax[i] = life;
    this.lh[i] = h;
    this.lw[i] = w;
    this.lcol[i * 3] = this.rgb[0] as number;
    this.lcol[i * 3 + 1] = this.rgb[1] as number;
    this.lcol[i * 3 + 2] = this.rgb[2] as number;
  }

  private schedule(delay: number, comp: ShotImpact, x: number, z: number, k: number): void {
    this.scheduleRaw(delay, IMPACT_ORDER.indexOf(comp), x, z, k);
  }

  private scheduleRaw(delay: number, comp: number, x: number, z: number, k: number): void {
    for (let i = 0; i < SCHED_CAP; i++) {
      if (this.sActive[i]) continue;
      this.sActive[i] = 1;
      this.sAt[i] = this.clock + delay;
      this.sComp[i] = comp;
      this.sx[i] = x;
      this.sz[i] = z;
      this.sk[i] = k;
      return;
    }
  }

  // ------------------------------------------------------------------ bouche du canon

  private muzzle(catId: number, x: number, y: number, angle: number): void {
    const s = this.styleOf();
    if (!s || s.muzzle === 'none' || this.clock - this.lastMuzzle < MIN_MUZZLE_GAP || this.load() > 0.9) return;
    this.lastMuzzle = this.clock;
    this.setStyle(s);
    const m = this.muzzleOf(catId);
    const fx = Math.cos(angle);
    const fz = -Math.sin(angle);
    const px = (m ? m.x : x) + fx * 0.25;
    const py = m ? m.y : 0.6;
    const pz = (m ? m.z : -y) + fz * 0.25;
    this.muzzleAt(s, px, py, pz, fx, fz, angle);
  }

  private muzzleAt(s: ShotFxStyle, px: number, py: number, pz: number, fx: number, fz: number, angle: number): void {
    const k = s.scale;
    const E = this.eco ? 0.5 : 1;
    const side = [-fz, fx] as const;
    switch (s.muzzle) {
      case 'burst':
        this.sp(s, 0, Cell.Glow, px, py, pz, 0, 0, 0, 0.16, 0.5 * k, 1.2 * k, 0.8);
        this.white(Cell.Disc, px, py, pz, 0, 0, 0, 0.1, 0.22 * k, 0.05, 1);
        for (let i = 0; i < 9 * E; i++) {
          const a = rnd(-0.7, 0.7);
          const sp = rnd(6, 13) * k;
          const dx = fx * Math.cos(a) - fz * Math.sin(a);
          const dz = fz * Math.cos(a) + fx * Math.sin(a);
          this.sp(s, i % 3, Cell.Streak, px, py, pz, dx * sp, rnd(-0.5, 1.5), dz * sp, rnd(0.14, 0.26), 0.45 * k, 0.08, 0.95, 0, 3, 0, 0, M_STREAK);
        }
        break;
      case 'cone':
        this.sp(s, 0, Cell.Glow, px, py, pz, 0, 0, 0, 0.18, 0.6 * k, 1.5 * k, 0.75);
        for (let i = 0; i < 12 * E; i++) {
          const a = rnd(-0.32, 0.32);
          const sp = rnd(5, 11) * k;
          const dx = fx * Math.cos(a) - fz * Math.sin(a);
          const dz = fz * Math.cos(a) + fx * Math.sin(a);
          this.sp(s, i % 2, Cell.Flame, px + dx * 0.2, py, pz + dz * 0.2, dx * sp, rnd(0.2, 1.4), dz * sp, rnd(0.2, 0.38), 0.3 * k, 0.04, 0.9, -1.5, 2, 0, 0, M_BILL);
        }
        break;
      case 'rings':
        for (let i = 0; i < 3; i++) {
          const j = this.spawnFacing(s, px + fx * i * 0.35, py, pz + fz * i * 0.35, fx, fz, 0.3 + i * 0.05, 0.25 * k, 1.5 * k + i * 0.3, 0.8 - i * 0.15, i === 1 ? 1 : 0);
          void j;
        }
        this.sp(s, 0, Cell.Glow, px, py, pz, 0, 0, 0, 0.12, 0.4 * k, 1.0 * k, 0.7);
        break;
      case 'star':
        this.sp(s, 0, Cell.Star, px, py, pz, 0, 0, 0, 0.2, 0.15 * k, 1.1 * k, 0.95, 0, 0, angle, 9);
        this.white(Cell.Spark, px, py, pz, 0, 0, 0, 0.16, 0.2 * k, 1.0 * k, 0.95, 0, 0, angle + 0.8, -6);
        this.sp(s, 1, Cell.Glow, px, py, pz, 0, 0, 0, 0.2, 0.3 * k, 1.4 * k, 0.55);
        break;
      case 'cannon':
        for (let i = 0; i < 16 * E; i++) {
          const a = rnd(-0.4, 0.4);
          const sp = rnd(5, 12);
          const dx = fx * Math.cos(a) - fz * Math.sin(a);
          const dz = fz * Math.cos(a) + fx * Math.sin(a);
          this.sp(s, i % 3, Cell.Square, px, py, pz, dx * sp, rnd(0.5, 3), dz * sp, rnd(0.5, 0.8), 0.1 * k, 0.07 * k, 0.95, 9, 1.6, Math.random() * 6, rnd(-14, 14));
        }
        this.white(Cell.Smoke, px, py, pz, fx, 0.4, fz, 0.4, 0.2 * k, 0.8 * k, 0.6);
        break;
      case 'spiral':
        for (let i = 0; i < 12 * E; i++) {
          const a = (i / 12) * TAU * 1.5;
          const sp = 3 + i * 0.5;
          const sx = Math.cos(a) * side[0] * 3;
          const sz = Math.cos(a) * side[1] * 3;
          const sy = Math.sin(a) * 3;
          this.sp(s, i % 2, Cell.Disc, px, py, pz, fx * sp + sx, sy, fz * sp + sz, 0.3, 0.1 * k, 0.03, 0.95, 0, 1.2);
        }
        this.sp(s, 0, Cell.Glow, px, py, pz, 0, 0, 0, 0.14, 0.4 * k, 1.1 * k, 0.65);
        break;
      case 'prism':
        for (let i = 0; i < 7; i++) {
          const a = (i - 3) * 0.2;
          const dx = fx * Math.cos(a) - fz * Math.sin(a);
          const dz = fz * Math.cos(a) + fx * Math.sin(a);
          hsv(i / 7 + this.clock * 0.3, 0.9, 1, this.rgb);
          this.spawn(Cell.Streak, px, py, pz, dx * 11 * k, 0, dz * 11 * k, 0.2, 0.55 * k, 0.1, this.rgb[0] as number, this.rgb[1] as number, this.rgb[2] as number, 0.95, 0, 3, 0, 0, M_STREAK);
        }
        this.white(Cell.Glow, px, py, pz, 0, 0, 0, 0.14, 0.4 * k, 1.1 * k, 0.7);
        break;
      case 'none':
        break;
    }
  }

  /** Anneau vertical qui avance dans le sens du tir (onde supersonique), face a la direction (fx, fz). */
  private spawnFacing(s: ShotFxStyle, x: number, y: number, z: number, fx: number, fz: number, life: number, r0: number, r1: number, a: number, which: number): number {
    this.col(s, which, this.n);
    const i = this.spawn(Cell.Ring, x, y, z, fx * 4, 0, fz * 4, life, r0, r1, this.rgb[0] as number, this.rgb[1] as number, this.rgb[2] as number, a, 0, 0, 0, 0, M_FACING);
    if (i >= 0) {
      this.ex[i] = fx;
      this.ez[i] = fz;
    }
    return i;
  }

  // ------------------------------------------------------------------ traine

  /** Appelee (echantillonnee) pour chaque projectile en vol : position Three, direction horizontale. */
  trail(x: number, y: number, z: number, dx: number, dz: number): void {
    const s = this.styleOf();
    if (!s || s.trail === 'none' || this.load() > 0.82) return;
    this.setStyle(s);
    const k = s.scale;
    const px = -dz;
    const pz = dx;
    const t = this.clock;
    switch (s.trail) {
      case 'comet':
        this.sp(s, 0, Cell.Glow, x, y, z, 0, 0, 0, 0.36, 0.55 * k, 0.1, 0.55);
        this.white(Cell.Glow, x, y, z, 0, 0, 0, 0.22, 0.28 * k, 0.05, 0.8);
        if (Math.random() < 0.5) this.sp(s, 1, Cell.Spark, x + rnd(-0.2, 0.2), y + rnd(-0.1, 0.2), z + rnd(-0.2, 0.2), -dx * 1.5, rnd(0, 1), -dz * 1.5, 0.3, 0.2 * k, 0, 0.9, 0, 0, Math.random() * 3, 5);
        break;
      case 'orbs': {
        const o = Math.sin(t * 20) * 0.42 * k;
        this.sp(s, 0, Cell.Disc, x + px * o, y + Math.cos(t * 20) * 0.3 * k, z + pz * o, 0, 0, 0, 0.4, 0.17 * k, 0.02, 0.95);
        this.sp(s, 1, Cell.Disc, x - px * o, y - Math.cos(t * 20) * 0.3 * k, z - pz * o, 0, 0, 0, 0.4, 0.17 * k, 0.02, 0.95);
        this.sp(s, 2, Cell.Glow, x, y, z, 0, 0, 0, 0.3, 0.45 * k, 0.1, 0.4);
        break;
      }
      case 'helix': {
        const o = Math.sin(t * 24) * 0.45 * k;
        const h = Math.cos(t * 24) * 0.35 * k;
        this.sp(s, 0, Cell.Disc, x + px * o, y + h, z + pz * o, 0, 0, 0, 0.45, 0.16 * k, 0.02, 0.95);
        this.sp(s, 1, Cell.Disc, x - px * o, y - h, z - pz * o, 0, 0, 0, 0.45, 0.16 * k, 0.02, 0.95);
        this.sp(s, 2, Cell.Glow, x, y, z, 0, 0, 0, 0.3, 0.35 * k, 0.05, 0.4);
        break;
      }
      case 'zigzag': {
        const o = rnd(-0.55, 0.55) * k;
        this.white(Cell.Spark, x + px * o, y + rnd(-0.15, 0.2), z + pz * o, 0, 0, 0, 0.18, 0.4 * k, 0.05, 0.95, 0, 0, Math.random() * 3, rnd(-8, 8));
        this.sp(s, 0, Cell.Glow, x, y, z, 0, 0, 0, 0.28, 0.4 * k, 0.08, 0.5);
        break;
      }
      case 'embers':
        this.sp(s, 0, Cell.Flame, x + rnd(-0.2, 0.2), y, z + rnd(-0.2, 0.2), rnd(-0.4, 0.4), rnd(1, 2.2), rnd(-0.4, 0.4), 0.45, 0.32 * k, 0.05, 0.9, -1.5, 0.5);
        this.sp(s, 1, Cell.Disc, x + rnd(-0.3, 0.3), y, z + rnd(-0.3, 0.3), rnd(-1, 1), rnd(1, 3), rnd(-1, 1), 0.55, 0.1 * k, 0.02, 0.9, 5, 0.4);
        this.sp(s, 2, Cell.Smoke, x, y, z, 0, 0.5, 0, 0.5, 0.2 * k, 0.55 * k, 0.28);
        break;
      case 'stars':
        this.sp(s, 0, Cell.Star, x + rnd(-0.3, 0.3), y + rnd(-0.1, 0.3), z + rnd(-0.3, 0.3), rnd(-0.6, 0.6), rnd(0.2, 1.2), rnd(-0.6, 0.6), 0.55, 0.26 * k, 0.04, 0.95, 0, 0.5, Math.random() * 6, rnd(-9, 9));
        this.white(Cell.Spark, x, y, z, 0, 0, 0, 0.2, 0.3 * k, 0.05, 0.9, 0, 0, Math.random() * 3, 7);
        break;
      case 'pixels':
        for (let i = 0; i < 2; i++) {
          const q = Math.round(rnd(-3, 3)) * 0.15 * k;
          this.sp(s, i, Cell.Square, x + px * q, y + Math.round(rnd(-2, 2)) * 0.12, z + pz * q, 0, 0, 0, 0.32, 0.14 * k, 0.12 * k, 0.95);
        }
        break;
      case 'rings':
        if (Math.random() < 0.6) this.spawnFacing(s, x, y, z, dx, dz, 0.35, 0.15 * k, 1.0 * k, 0.8, 0);
        this.white(Cell.Glow, x, y, z, 0, 0, 0, 0.18, 0.3 * k, 0.05, 0.6);
        break;
      case 'petals':
        this.sp(s, 0, Cell.Petal, x + rnd(-0.3, 0.3), y + rnd(0, 0.3), z + rnd(-0.3, 0.3), rnd(-1, 1), rnd(0, 1), rnd(-1, 1), 0.7, 0.28 * k, 0.1, 0.95, 1.5, 1, Math.random() * 6, rnd(-7, 7));
        this.sp(s, 1, Cell.Petal, x + rnd(-0.3, 0.3), y, z + rnd(-0.3, 0.3), rnd(-1, 1), rnd(0, 1), rnd(-1, 1), 0.6, 0.2 * k, 0.05, 0.9, 1.5, 1, Math.random() * 6, rnd(-9, 9));
        break;
      case 'bubbles':
        this.sp(s, 0, Cell.Bubble, x + rnd(-0.3, 0.3), y + rnd(-0.1, 0.2), z + rnd(-0.3, 0.3), rnd(-0.4, 0.4), rnd(0.6, 1.6), rnd(-0.4, 0.4), 0.8, 0.22 * k, 0.3 * k, 0.9, -0.6, 0.8);
        this.sp(s, 1, Cell.Bubble, x + rnd(-0.4, 0.4), y, z + rnd(-0.4, 0.4), 0, rnd(0.4, 1.2), 0, 0.7, 0.12 * k, 0.18 * k, 0.85, -0.5, 0.8);
        break;
      case 'hearts':
        this.sp(s, 0, Cell.Heart, x + rnd(-0.3, 0.3), y + rnd(0, 0.3), z + rnd(-0.3, 0.3), rnd(-0.5, 0.5), rnd(0.8, 1.8), rnd(-0.5, 0.5), 0.75, 0.3 * k, 0.12 * k, 0.95, -0.6, 0.8, rnd(-0.4, 0.4));
        break;
      case 'drips':
        this.sp(s, 0, Cell.Disc, x + rnd(-0.2, 0.2), y, z + rnd(-0.2, 0.2), rnd(-0.3, 0.3), 0, rnd(-0.3, 0.3), 0.6, 0.2 * k, 0.07 * k, 0.9, 8, 0.5);
        this.sp(s, 1, Cell.Smoke, x, y, z, 0, 0.3, 0, 0.5, 0.25 * k, 0.7 * k, 0.32);
        break;
      case 'confetti':
        for (let i = 0; i < 2; i++) {
          this.sp(s, i + this.n, Cell.Square, x + rnd(-0.3, 0.3), y, z + rnd(-0.3, 0.3), rnd(-1.2, 1.2), rnd(0.5, 2), rnd(-1.2, 1.2), 0.75, 0.13 * k, 0.09 * k, 0.95, 6, 0.8, Math.random() * 6, rnd(-14, 14));
        }
        break;
      case 'prism': {
        hsv(t * 0.6, 0.9, 1, this.rgb);
        this.spawn(Cell.Glow, x, y, z, 0, 0, 0, 0.32, 0.5 * k, 0.1, this.rgb[0] as number, this.rgb[1] as number, this.rgb[2] as number, 0.6);
        hsv(t * 0.6 + 0.5, 0.9, 1, this.rgb);
        this.spawn(Cell.Spark, x + rnd(-0.2, 0.2), y, z + rnd(-0.2, 0.2), 0, 0, 0, 0.25, 0.3 * k, 0.05, this.rgb[0] as number, this.rgb[1] as number, this.rgb[2] as number, 0.9, 0, 0, Math.random() * 3, 6);
        break;
      }
    }
  }

  /** Faisceau (laser) : ruban lumineux + impact a l'extremite. */
  private beam(x0: number, y0: number, x1: number, y1: number): void {
    const s = this.styleOf();
    if (!s) return;
    this.setStyle(s);
    const len = Math.hypot(x1 - x0, y1 - y0);
    const dx = (x1 - x0) / (len || 1);
    const dz = -(y1 - y0) / (len || 1);
    const steps = Math.min(10, Math.floor(len / 1.3));
    if (s.trail !== 'none') for (let i = 0; i < steps; i++) this.trail(x0 + (x1 - x0) * ((i + Math.random()) / steps), 0.7, -(y0 + (y1 - y0) * ((i + Math.random()) / steps)), dx, dz);
    this.impact(x1, -y1, 1.1);
  }

  // ------------------------------------------------------------------ impacts

  /** Impact : execute (ou programme) chaque brique du FX equipe. `x`, `z` en coordonnees Three. */
  private impact(x: number, z: number, mul: number, force = false): void {
    const s = this.styleOf();
    if (!s || s.impact.length === 0) return;
    if (!force && this.clock - this.lastImpact < MIN_IMPACT_GAP) return;
    if (this.load() > 0.85 && !force) return;
    this.lastImpact = this.clock;
    this.setStyle(s);
    const k = s.scale * mul;
    // une implosion retarde toutes les autres briques (elles sont l'explosion qui suit)
    const base = s.impact.includes('implode') ? 0.42 : 0;
    s.impact.forEach((comp, i) => {
      if (comp === 'implode') this.run(comp, x, z, k);
      else if (base > 0 || comp === 'crater' || i > 3) this.schedule(base + (comp === 'crater' ? 0.1 : i * 0.045), comp, x, z, k);
      else this.run(comp, x, z, k);
    });
  }

  private run(comp: ShotImpact, x: number, z: number, k: number): void {
    const s = this.cur;
    if (!s) return;
    const E = this.eco ? 0.5 : 1;
    const y = 0.5;
    switch (comp) {
      case 'fireball':
        this.sp(s, 0, Cell.Glow, x, y, z, 0, 0.8, 0, 0.55, 0.5 * k, 2.4 * k, 0.75);
        this.sp(s, 1, Cell.Glow, x, y + 0.1, z, 0, 1.0, 0, 0.45, 0.35 * k, 1.8 * k, 0.8);
        this.sp(s, 2, Cell.Disc, x, y, z, 0, 0.6, 0, 0.3, 0.55 * k, 0.1, 0.9);
        this.white(Cell.Disc, x, y, z, 0, 0.5, 0, 0.18, 0.4 * k, 0.05, 1);
        for (let i = 0; i < 10 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(2, 7) * k;
          this.sp(s, i % 3, Cell.Flame, x, y, z, Math.cos(a) * sp, rnd(1, 5), Math.sin(a) * sp, rnd(0.3, 0.6), rnd(0.25, 0.45) * k, 0.03, 0.9, -1, 2.2);
        }
        break;
      case 'shockwave':
        this.ring(x, z, 0.55, 0.3 * k, 3.6 * k, s, 1, 0.85, Cell.Ring);
        this.ring(x, z, 0.4, 0.2 * k, 2.4 * k, s, 0, 0.7, Cell.Pulse);
        this.sp(s, 1, Cell.Ring, x, y + 0.2, z, 0, 0, 0, 0.4, 0.3 * k, 2.2 * k, 0.7); // anneau face camera
        break;
      case 'pillar':
        this.pillar(x, z, 0.6, 6.5 * k, 0.55 * k, s, 0);
        this.pillar(x, z, 0.45, 5.0 * k, 0.28 * k, s, 2);
        break;
      case 'shards':
        for (let i = 0; i < 14 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(4, 11) * k;
          this.sp(s, i % 3, Cell.Shard, x, y, z, Math.cos(a) * sp, rnd(2, 8), Math.sin(a) * sp, rnd(0.5, 0.9), 0.5 * k, 0.12 * k, 0.95, 16, 0.5, 0, 0, M_STREAK);
        }
        break;
      case 'bolts':
        for (let i = 0; i < 6; i++) this.bolt(x, z, (i / 6) * TAU + rnd(-0.3, 0.3), rnd(2.6, 4.2) * k, 0.26, s, i % 2 === 0 ? 0 : 1);
        this.white(Cell.Glow, x, y, z, 0, 0, 0, 0.2, 0.8 * k, 1.6 * k, 0.8);
        break;
      case 'implode': {
        // particules aspirees vers le centre, noyau sombre qui grossit
        for (let i = 0; i < 26 * E; i++) {
          const a = Math.random() * TAU;
          const d = rnd(2.4, 4.2) * k;
          const life = rnd(0.32, 0.42);
          const j = this.sp(s, i % 3, i % 4 === 0 ? Cell.Star : Cell.Disc, x + Math.cos(a) * d, y + rnd(-0.2, 0.9), z + Math.sin(a) * d, (-Math.cos(a) * d) / life, 0, (-Math.sin(a) * d) / life, life, 0.2 * k, 0.03, 0.95);
          void j;
        }
        this.spawn(Cell.Disc, x, y, z, 0, 0, 0, 0.42, 0.1, 0.95 * k, 0.05, 0.0, 0.1, 0.95);
        this.sp(s, 0, Cell.Glow, x, y, z, 0, 0, 0, 0.42, 0.4 * k, 1.9 * k, 0.5);
        this.ring(x, z, 0.42, 3.8 * k, 0.4 * k, s, 0, 0.8, Cell.Vortex, 6);
        break;
      }
      case 'meteors': {
        const n = Math.round(6 * E);
        for (let i = 0; i < n; i++) {
          const a = Math.random() * TAU;
          const d = Math.sqrt(Math.random()) * 2.3 * k;
          const tx = x + Math.cos(a) * d;
          const tz = z + Math.sin(a) * d;
          // chaque meteore est programme : il part de haut au bon moment (METEOR_LAUNCH), tombe en diagonale, puis explose
          this.scheduleRaw(i * 0.07 + rnd(0, 0.12), METEOR_LAUNCH, tx, tz, k);
        }
        break;
      }
      case 'crater':
        this.ring(x, z, 1.8, 1.6 * k, 2.0 * k, s, 2, 0.5, Cell.Disc);
        this.ring(x, z, 1.2, 1.1 * k, 1.9 * k, s, 0, 0.85, Cell.Ring);
        this.ring(x, z, 0.9, 0.9 * k, 1.5 * k, s, 1, 0.5, Cell.Glow);
        break;
      case 'bloom':
        for (let i = 0; i < 16 * E; i++) {
          const a = (i / 16) * TAU;
          const sp = rnd(2.5, 5) * k;
          this.sp(s, i % 2, Cell.Petal, x, y, z, Math.cos(a) * sp, rnd(3, 6), Math.sin(a) * sp, rnd(0.8, 1.2), 0.34 * k, 0.1, 0.95, 5, 1.4, Math.random() * 6, rnd(-8, 8));
        }
        this.ring(x, z, 0.9, 0.3 * k, 2.6 * k, s, 0, 0.9, Cell.Lotus, 2);
        break;
      case 'spikes':
        for (let i = 0; i < 11; i++) {
          const a = (i / 11) * TAU + rnd(-0.15, 0.15);
          const d = rnd(0.8, 1.7) * k;
          const h = rnd(0.7, 1.4) * k;
          // pic = triangle vertical qui pousse (taille 0 -> h -> 0), pose debout au sol
          this.sp(s, i % 2, Cell.Triangle, x + Math.cos(a) * d, h * 0.5, z + Math.sin(a) * d, 0, 0, 0, 0.7, h * 0.5, h * 0.05, 0.92, 0, 0, 0, 0, M_BILL);
        }
        this.ring(x, z, 0.6, 0.3 * k, 2.3 * k, s, 2, 0.8, Cell.Ring);
        break;
      case 'bubbles':
        for (let i = 0; i < 14 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(0.6, 3) * k;
          this.sp(s, i % 3, Cell.Bubble, x, y, z, Math.cos(a) * sp, rnd(1, 4), Math.sin(a) * sp, rnd(0.9, 1.4), rnd(0.18, 0.4) * k, rnd(0.25, 0.5) * k, 0.9, -1, 1.2);
        }
        break;
      case 'confetti':
        for (let i = 0; i < 26 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(2, 7) * k;
          this.sp(s, i % 3, Cell.Square, x, y, z, Math.cos(a) * sp, rnd(3, 9), Math.sin(a) * sp, rnd(0.8, 1.3), 0.14 * k, 0.1 * k, 0.95, 11, 0.8, Math.random() * 6, rnd(-16, 16));
        }
        break;
      case 'hearts':
        for (let i = 0; i < 10 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(1, 4) * k;
          this.sp(s, i % 3, Cell.Heart, x, y, z, Math.cos(a) * sp, rnd(2, 5), Math.sin(a) * sp, rnd(0.9, 1.4), rnd(0.28, 0.5) * k, 0.12 * k, 0.95, -0.4, 0.9, rnd(-0.4, 0.4));
        }
        break;
      case 'stars':
        for (let i = 0; i < 13 * E; i++) {
          const a = Math.random() * TAU;
          const sp = rnd(3, 9) * k;
          this.sp(s, i % 3, i % 3 === 2 ? Cell.Spark : Cell.Star, x, y, z, Math.cos(a) * sp, rnd(2, 7), Math.sin(a) * sp, rnd(0.6, 1.0), rnd(0.25, 0.45) * k, 0.05, 0.95, 9, 0.9, Math.random() * 6, rnd(-9, 9));
        }
        break;
      case 'nova':
        this.ring(x, z, 0.7, 0.3 * k, 3.6 * k, s, 0, 0.9, Cell.StarRune, 3);
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * TAU;
          this.white(Cell.Spark, x, y, z, Math.cos(a) * 8 * k, 0, Math.sin(a) * 8 * k, 0.3, 0.55 * k, 0.05, 0.9, 0, 3, a, 0, M_STREAK);
        }
        break;
      case 'smokering':
        for (let i = 0; i < 12 * E; i++) {
          const a = (i / 12) * TAU;
          this.sp(s, 2, Cell.Smoke, x, y, z, Math.cos(a) * 4 * k, rnd(0.5, 1.4), Math.sin(a) * 4 * k, 0.95, 0.35 * k, 0.95 * k, 0.5, 0, 2.4);
        }
        this.sp(s, 2, Cell.Smoke, x, y + 0.6, z, 0, 2, 0, 1.0, 0.6 * k, 1.5 * k, 0.45);
        break;
      case 'tornado':
        for (let i = 0; i < 22 * E; i++) {
          const j = this.sp(s, i % 2, i % 3 === 0 ? Cell.Flame : Cell.Disc, x, 0.15, z, 0, rnd(2.2, 4.2), 0, rnd(0.55, 0.85), rnd(0.2, 0.34) * k, 0.04, 0.9, 0, 0, 0, 0, M_ORBIT);
          if (j >= 0) {
            this.ex[j] = x;
            this.ez[j] = z;
            this.rot[j] = (i / 22) * TAU;
            this.vx[j] = rnd(7, 11) * (i % 2 ? 1 : 0.8);
            this.orad[j] = rnd(0.2, 0.7) * k;
            this.vz[j] = rnd(1.2, 2.2) * k;
          }
        }
        break;
      case 'pixels':
        for (let i = 0; i < 18 * E; i++) {
          const a = Math.round(rnd(0, 7)) * (TAU / 8);
          const sp = rnd(3, 9) * k;
          const q = 0.18 * k * (1 + Math.round(rnd(0, 2)));
          this.sp(s, i % 3, Cell.Square, x, y, z, Math.cos(a) * sp, Math.round(rnd(0, 4)) * 1.2, Math.sin(a) * sp, rnd(0.3, 0.6), q, q * 0.4, 1, 4, 1);
        }
        this.ring(x, z, 0.5, 0.4 * k, 3.0 * k, s, 0, 0.8, Cell.Checker);
        break;
      case 'rainbowrings':
        for (let i = 0; i < 4; i++) {
          this.col({ ...s, rainbow: true }, i, i * 3);
          const idx = this.rCursor % RING_CAP;
          this.ring(x, z, 0.5 + i * 0.08, (0.3 + i * 0.1) * k, (2.0 + i * 0.7) * k, s, i, 0.85, Cell.Ring);
          this.rcol[idx * 3] = this.rgb[0] as number;
          this.rcol[idx * 3 + 1] = this.rgb[1] as number;
          this.rcol[idx * 3 + 2] = this.rgb[2] as number;
          this.rrain[idx] = 0;
        }
        this.white(Cell.Glow, x, y, z, 0, 0, 0, 0.3, 0.6 * k, 1.8 * k, 0.6);
        break;
    }
  }

  /** Meteore : tete flamboyante qui s'abat en diagonale sur (tx, tz) en `fall` secondes ; l'explosion est programmee a l'arrivee. */
  private launchMeteor(tx: number, tz: number, k: number): void {
    const s = this.cur;
    if (!s) return;
    const fall = rnd(0.28, 0.42);
    const sx = tx - 5 * k;
    const sz = tz + 3 * k;
    const sy = 9 * k;
    this.sp(s, 0, Cell.Flame, sx, sy, sz, (tx - sx) / fall, (0.4 - sy) / fall, (tz - sz) / fall, fall, 0.6 * k, 0.45 * k, 0.95, 0, 0, 0, 0, M_STREAK);
    this.white(Cell.Glow, sx, sy, sz, (tx - sx) / fall, (0.4 - sy) / fall, (tz - sz) / fall, fall, 0.4 * k, 0.3 * k, 0.7);
    this.schedule(fall, 'fireball', tx, tz, k * 0.45);
  }

  // ------------------------------------------------------------------ mise a jour + dessin

  update(dt: number, camera: PerspectiveCamera): void {
    const step = Math.min(dt, 0.05);
    this.clock += step;
    // composants differes
    for (let i = 0; i < SCHED_CAP; i++) {
      if (this.sActive[i] && (this.sAt[i] as number) <= this.clock) {
        this.sActive[i] = 0;
        const comp = this.sComp[i] as number;
        if (comp === METEOR_LAUNCH) this.launchMeteor(this.sx[i] as number, this.sz[i] as number, this.sk[i] as number);
        else this.run(IMPACT_ORDER[comp] as ShotImpact, this.sx[i] as number, this.sz[i] as number, this.sk[i] as number);
      }
    }
    camera.updateMatrixWorld();
    const cam = this.cam;
    cam.pos.copy(camera.position);
    cam.right.set(1, 0, 0).applyQuaternion(camera.quaternion);
    cam.up.set(0, 1, 0).applyQuaternion(camera.quaternion);
    this.sprites.begin();
    this.ribbons.begin();
    this.drawParticles(step, cam);
    this.drawRings(step, cam);
    this.drawPillars(step, cam);
    this.drawBolts(step, cam);
    this.sprites.end();
    this.ribbons.end();
  }

  private drawParticles(dt: number, cam: CamBasis): void {
    const sp = this.sprites;
    let i = 0;
    while (i < this.n) {
      const life = (this.life[i] as number) - dt;
      if (life <= 0) {
        // compactage : derniere particule dans le trou
        const last = --this.n;
        if (i !== last) this.moveParticle(last, i);
        continue;
      }
      this.life[i] = life;
      const m = this.mode[i] as number;
      if (m === M_ORBIT) {
        this.rot[i] = (this.rot[i] as number) + (this.vx[i] as number) * dt;
        this.orad[i] = (this.orad[i] as number) + (this.vz[i] as number) * dt;
        this.px[i] = (this.ex[i] as number) + Math.cos(this.rot[i] as number) * (this.orad[i] as number);
        this.pz[i] = (this.ez[i] as number) + Math.sin(this.rot[i] as number) * (this.orad[i] as number);
        this.py[i] = (this.py[i] as number) + (this.vy[i] as number) * dt;
      } else {
        const dr = Math.max(0, 1 - (this.drag[i] as number) * dt);
        this.vx[i] = (this.vx[i] as number) * dr;
        this.vz[i] = (this.vz[i] as number) * dr;
        this.vy[i] = (this.vy[i] as number) * dr - (this.grav[i] as number) * dt;
        this.px[i] = (this.px[i] as number) + (this.vx[i] as number) * dt;
        this.py[i] = (this.py[i] as number) + (this.vy[i] as number) * dt;
        this.pz[i] = (this.pz[i] as number) + (this.vz[i] as number) * dt;
        this.rot[i] = (this.rot[i] as number) + (this.spin[i] as number) * dt;
      }
      if ((this.py[i] as number) < 0.04 && (this.grav[i] as number) > 0) {
        this.py[i] = 0.04;
        this.vy[i] = Math.abs(this.vy[i] as number) * 0.25;
      }
      const t = life / (this.maxLife[i] as number);
      const size = (this.s1[i] as number) + ((this.s0[i] as number) - (this.s1[i] as number)) * t;
      // apparition rapide, disparition douce
      const a = (this.ca[i] as number) * Math.min(1, t * 3.2, (1 - t) * 14 + 0.15);
      const x = this.px[i] as number;
      const y = this.py[i] as number;
      const z = this.pz[i] as number;
      const cell = this.cell[i] as Cell;
      if (m === M_STREAK) {
        const vx = this.vx[i] as number;
        const vy = this.vy[i] as number;
        const vz = this.vz[i] as number;
        const l = Math.hypot(vx, vy, vz) || 1;
        sp.oriented(cam, cell, x, y, z, vx / l, vy / l, vz / l, size * (cell === Cell.Shard ? 1 : 1.6), size * 0.55, this.cr[i] as number, this.cg[i] as number, this.cb[i] as number, a);
      } else if (m === M_FACING) {
        const fx = this.ex[i] as number;
        const fz = this.ez[i] as number;
        // anneau vertical perpendiculaire a la direction (fx, fz)
        sp.quad(cell, x, y, z, -fz * size, 0, fx * size, 0, size, 0, this.cr[i] as number, this.cg[i] as number, this.cb[i] as number, a);
      } else sp.billboard(cam, cell, x, y, z, size, this.rot[i] as number, this.cr[i] as number, this.cg[i] as number, this.cb[i] as number, a);
      i++;
    }
  }

  private moveParticle(from: number, to: number): void {
    this.px[to] = this.px[from] as number;
    this.py[to] = this.py[from] as number;
    this.pz[to] = this.pz[from] as number;
    this.vx[to] = this.vx[from] as number;
    this.vy[to] = this.vy[from] as number;
    this.vz[to] = this.vz[from] as number;
    this.life[to] = this.life[from] as number;
    this.maxLife[to] = this.maxLife[from] as number;
    this.s0[to] = this.s0[from] as number;
    this.s1[to] = this.s1[from] as number;
    this.rot[to] = this.rot[from] as number;
    this.spin[to] = this.spin[from] as number;
    this.cell[to] = this.cell[from] as number;
    this.cr[to] = this.cr[from] as number;
    this.cg[to] = this.cg[from] as number;
    this.cb[to] = this.cb[from] as number;
    this.ca[to] = this.ca[from] as number;
    this.grav[to] = this.grav[from] as number;
    this.drag[to] = this.drag[from] as number;
    this.mode[to] = this.mode[from] as number;
    this.ex[to] = this.ex[from] as number;
    this.ez[to] = this.ez[from] as number;
    this.orad[to] = this.orad[from] as number;
  }

  private drawRings(dt: number, cam: CamBasis): void {
    void cam;
    const sp = this.sprites;
    for (let i = 0; i < RING_CAP; i++) {
      if (!this.rActive[i]) continue;
      const t = (this.rt[i] as number) + dt;
      this.rt[i] = t;
      const u = t / (this.rmax[i] as number);
      if (u >= 1) {
        this.rActive[i] = 0;
        continue;
      }
      const e = 1 - (1 - u) * (1 - u);
      const r = (this.rr0[i] as number) + ((this.rr1[i] as number) - (this.rr0[i] as number)) * e;
      let cr = this.rcol[i * 3] as number;
      let cg = this.rcol[i * 3 + 1] as number;
      let cb = this.rcol[i * 3 + 2] as number;
      if (this.rrain[i]) {
        hsv(this.clock * 0.9 + i * 0.13, 0.9, 1, this.rgb);
        cr = this.rgb[0] as number;
        cg = this.rgb[1] as number;
        cb = this.rgb[2] as number;
      }
      const a = (this.ralpha[i] as number) * (1 - u) * Math.min(1, u * 12);
      sp.flat(this.rcell[i] as Cell, this.rx[i] as number, FLAT_Y + i * 0.0005, this.rz[i] as number, r, (this.rspin[i] as number) * t, cr, cg, cb, a);
    }
  }

  private drawPillars(dt: number, cam: CamBasis): void {
    const sp = this.sprites;
    for (let i = 0; i < PILLAR_CAP; i++) {
      if (!this.lActive[i]) continue;
      const t = (this.lt[i] as number) + dt;
      this.lt[i] = t;
      const u = t / (this.lmax[i] as number);
      if (u >= 1) {
        this.lActive[i] = 0;
        continue;
      }
      const grow = Math.min(1, u * 5);
      const h = (this.lh[i] as number) * grow;
      const w = (this.lw[i] as number) * (1 - u * 0.7);
      const a = (1 - u) * 0.85;
      const x = this.lx[i] as number;
      const z = this.lz[i] as number;
      sp.oriented(cam, Cell.Streak, x, h * 0.5, z, 0, 1, 0, h * 0.5, w, this.lcol[i * 3] as number, this.lcol[i * 3 + 1] as number, this.lcol[i * 3 + 2] as number, a * 0.7);
      sp.oriented(cam, Cell.Streak, x, h * 0.5, z, 0, 1, 0, h * 0.5, w * 0.4, 1, 1, 1, a);
      sp.billboard(cam, Cell.Glow, x, 0.4, z, w * 2.4, 0, this.lcol[i * 3] as number, this.lcol[i * 3 + 1] as number, this.lcol[i * 3 + 2] as number, a * 0.6);
    }
  }

  private drawBolts(dt: number, cam: CamBasis): void {
    for (let i = 0; i < BOLT_CAP; i++) {
      if (!this.bActive[i]) continue;
      const t = (this.bt[i] as number) + dt;
      this.bt[i] = t;
      const u = t / (this.bmax[i] as number);
      if (u >= 1) {
        this.bActive[i] = 0;
        continue;
      }
      const ang = this.bangle[i] as number;
      const len = this.blen[i] as number;
      const dx = Math.cos(ang);
      const dz = Math.sin(ang);
      const px = -dz;
      const pz = dx;
      const seed = (this.bseed[i] as number) + Math.floor(t * 40); // l'eclair « crepite » : nouvelle forme ~40 fois / s
      for (let k = 0; k <= BOLT_SEGMENTS; k++) {
        const f = k / BOLT_SEGMENTS;
        const off = k === 0 ? 0 : (Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453 % 1) * 0.55 * len * 0.25 * Math.sin(f * Math.PI);
        this.bxs[k] = (this.bx[i] as number) + dx * len * f + px * off;
        this.bys[k] = 0.5 + 0.3 * Math.sin(f * 6 + seed);
        this.bzs[k] = (this.bz[i] as number) + dz * len * f + pz * off;
      }
      const a = 1 - u;
      const cr = this.bcol[i * 3] as number;
      const cg = this.bcol[i * 3 + 1] as number;
      const cb = this.bcol[i * 3 + 2] as number;
      this.ribbons.strip(cam, BOLT_SEGMENTS + 1, this.bxs, this.bys, this.bzs, 0.22, 0.04, 0.55 * a, 0.1 * a, cr, cg, cb);
      this.ribbons.strip(cam, BOLT_SEGMENTS + 1, this.bxs, this.bys, this.bzs, 0.07, 0.02, a, 0.4 * a, 1, 1, 1);
    }
  }

  /** Vide tous les effets en cours (changement de FX / de mode). */
  clear(): void {
    this.n = 0;
    this.rActive.fill(0);
    this.bActive.fill(0);
    this.lActive.fill(0);
    this.sActive.fill(0);
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs = [];
    this.sprites.dispose();
    this.ribbons.dispose();
    this.atlas.dispose();
  }

  /** Declenche un FX a la main (galerie / apercu) : muzzle + impact. */
  preview(style: ShotFxStyle, sx: number, sz: number, tx: number, tz: number): void {
    this.setStyle(style);
    const dx = tx - sx;
    const dz = tz - sz;
    const l = Math.hypot(dx, dz) || 1;
    const prev = this.styleOf;
    this.styleOf = () => style;
    this.muzzleAt(style, sx, 0.8, sz, dx / l, dz / l, Math.atan2(-dz, dx));
    for (let i = 0; i < 5; i++) {
      const f = i / 5;
      this.trail(sx + dx * f, 0.8, sz + dz * f, dx / l, dz / l);
    }
    this.lastImpact = -1;
    this.impact(tx, tz, 1, true);
    this.styleOf = prev;
  }

  /** Dessine un seul tir de demonstration en continu (galerie) : appeler avant `update`. */
  previewTrail(style: ShotFxStyle, x: number, y: number, z: number, dx: number, dz: number): void {
    const prev = this.styleOf;
    this.styleOf = () => style;
    this.setStyle(style);
    this.trail(x, y, z, dx, dz);
    this.styleOf = prev;
  }
}
